import { Injectable } from '@angular/core';

import {
  catchError,
  map,
  Observable,
  of,
  Subscription,
  take,
} from 'rxjs';
import isEqual from 'lodash/isEqual';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

import { Party } from '@shared/party/models';
import { PartyTypes } from '@shared/party/enums';
import { AuthUser } from '@shared/signup/models';
import { Schedule } from '@shared/schedule/models';
import { Resource } from '@shared/resource/models';
import { UserLocationRole } from '@shared/user/enums';
import { FloorplanResources, IParty } from '@tablesready/common-ui';
import { FrontendModalService, FrontendModalSize } from '@tablesready/frontend-modal';
import { ResourcesService } from './resources.service';
import { WaitlistAddEditPartyFloorplanComponent } from '../components/waitlist-add-edit-party-floorplan';
import { PartiesUsageService } from './parties-usage.service';
import { PricePlansRestrictionsService } from '@tablesready/frontend-restrictions';
import { AddEditPartyComponent } from '../components/add-edit-party/add-edit-party.component';

type ModalInstance = WaitlistAddEditPartyFloorplanComponent;

export interface IPartyOnWaitlist extends IParty {
  date: NgbDateStruct;
  sizeOther: number | string;
  durationInMinutes: number;
  quoted_time_minutes: number | string;
}

@Injectable({ providedIn: 'root' })
export class WaitlistService {
  transparentClassName = 'transparent-ion-modal';
  private readonly waitlistModalsSubs = new Subscription();

  constructor(
    private readonly modalController: FrontendModalService,
    private readonly resourcesService: ResourcesService,
    private readonly partiesUsageService: PartiesUsageService,
    private readonly pricePlansRestrictionsService: PricePlansRestrictionsService,
  ) { }

  clearModalSubs(): void {
    this.waitlistModalsSubs.unsubscribe();
  }

  addEditParty(
    user: AuthUser,
    parties: Party[],
    schedules: Schedule[],
    selectedResource: Resource,
    floorPlanMode = false,
    floorplanResources?: FloorplanResources[],
    showSelectResourceMessage = false,
    overrideWithResourceId?: string,
    party?: Party,
    serveNow = false,
    showSuccessUpdateAlert = true,
    skipName?: boolean,
  ): Observable<Party | null> {
    const partyNoteElement = 'note';
    const settingsNoteElement = 'noteOptions';

    if (
      party &&
      party[partyNoteElement] &&
      this.isString(party[partyNoteElement]) &&
      this.isArray(user[settingsNoteElement]) &&
      user[settingsNoteElement].length > 0
    ) {
      const partyNoteEl = party[partyNoteElement] as string;
      party[partyNoteElement] = partyNoteEl.split(',');
    } else {
      const { usage } = user.location;

      // + 1 because we adding single party
      if (!party && usage.parties + 1 > usage.partiesLimit) {
        const isLocationManager = user.locationUser && user.locationUser.role === UserLocationRole.manager;

        return this.partiesUsageService.partiesLimitReached(usage.partiesLimit, isLocationManager);
      }
    }

    const component = WaitlistAddEditPartyFloorplanComponent;
    return this.modalController.create<WaitlistAddEditPartyFloorplanComponent, Party>(
      component,
      {},
      FrontendModalSize.default,
      false,
      (modalElement: HTMLIonModalElement) => {
        const componentProps: Partial<Record<keyof WaitlistAddEditPartyFloorplanComponent, unknown>> = {};

        if (!serveNow) {
          componentProps.selectedResource = selectedResource;
          componentProps.partyToEdit = party;
        }

        componentProps.showSelectResourceMessage = showSelectResourceMessage;
        componentProps.parties = parties;
        componentProps.skipName = skipName;
        componentProps.userSchedule = schedules;
        componentProps.typeOfParty = PartyTypes.walkIn;
        componentProps.user = {
          ...user.location,
          account: user.account,
        };
        componentProps.availableOnBusinessPlan = this.pricePlansRestrictionsService.availableForBusinessPlan(user.account.subscriptionType, user.location.recurly);

        if (floorplanResources) {
          componentProps.floorplanResources = floorplanResources;
        }

        if (serveNow && !overrideWithResourceId) {
          componentProps.afterInit = (instance: ModalInstance): void => {
            this.serveNowSettings(instance);
            this.showModal(instance, modalElement);
          };
        } else if (overrideWithResourceId) {
          modalElement.cssClass = `${modalElement.cssClass} ${this.transparentClassName}`;
          componentProps.afterInit = (instance: ModalInstance): void => {
            this.afterInitLogic(instance, modalElement, serveNow, party, overrideWithResourceId, selectedResource, showSuccessUpdateAlert);
          };
        }

        modalElement.componentProps = componentProps;
      },
    ).pipe(
      take(1),
      map((updatedParty: Party) => {
        if (!updatedParty) {
          return updatedParty;
        }

        return {
          ...party,
          ...updatedParty,
        };
      }),
      catchError(err => of(err)),
    );
  }

  // Is in use only on waitlist page
  addEditPartyReactive({
    user,
    parties,
    schedules,
    showSelectResourceMessage,
    prefillFields,
    party,
  }: AddEditPartyReactive): Observable<Party | null> {
    const partyNoteElement = 'note';
    const settingsNoteElement = 'noteOptions';

    if (
      party &&
      party[partyNoteElement] &&
      this.isString(party[partyNoteElement]) &&
      this.isArray(user[settingsNoteElement]) &&
      user[settingsNoteElement].length > 0
    ) {
      const partyNoteEl = party[partyNoteElement] as string;
      party[partyNoteElement] = partyNoteEl.split(',');
    } else {
      const { usage } = user.location;
      if (!party && usage.parties + 1 > usage.partiesLimit) {
        const isLocationManager = user.locationUser && user.locationUser.role === UserLocationRole.manager;

        return this.partiesUsageService.partiesLimitReached(usage.partiesLimit, isLocationManager);
      }
    }

    const component = AddEditPartyComponent;
    return this.modalController.create<AddEditPartyComponent, Party>(
      component,
      {},
      FrontendModalSize.default,
      false,
      (modalElement: HTMLIonModalElement) => {
        modalElement.componentProps = {
          showSelectResourceMessage,
          parties,
          prefillFields,
          userSchedule: schedules,
          user: {
            ...user.location,
            account: user.account,
          },
          partyToEdit: party,
          availableOnBusinessPlan: this.pricePlansRestrictionsService.availableForBusinessPlan(user.account.subscriptionType, user.location.recurly),
        };
      },
    ).pipe(
      take(1),
      map((updatedParty: Party) => {
        if (!updatedParty) {
          return updatedParty;
        }

        return {
          ...party,
          ...updatedParty,
        };
      }),
      catchError(err => of(err)),
    );
  }

  private readonly serveNowSettings = (instance: ModalInstance): void => {
    instance.skipConfirmation = true;
    instance.setQuotedTime();
    instance.confirmBtnText = 'Seat now';
    instance.title = `Add and serve a/an ${instance.user?.displayOptions?.labels?.customer || 'customer'} now`;
    instance.skipName = true;
  };

  private readonly closeModal = (instance: ModalInstance, showSuccessUpdateAlert = true): void => {
    instance.ok(showSuccessUpdateAlert);
  };

  private readonly showModal = (instance: ModalInstance, modal: HTMLIonModalElement): void => {
    instance.resourceOverrideCallback = undefined;
    instance.revealForm = undefined;
    instance.abortHandOff = undefined;
    if (typeof modal.cssClass === 'string') {
      modal.cssClass = modal.cssClass.replace(this.transparentClassName, '');
    }
  };

  private afterInitLogic(
    instance: ModalInstance,
    modalElement: HTMLIonModalElement,
    serveNow: boolean,
    party: Party,
    overrideWithResourceId: string,
    selectedResource: Resource,
    showSuccessUpdateAlert = true,
  ): void {
    if (!instance) {
      return;
    }

    if (serveNow) {
      this.serveNowSettings(instance);
    }

    instance.selectResources();
    instance.sizeChanged();
    instance.checkWorkTime();

    const resource = this.resourcesService.getById(overrideWithResourceId);
    if (!resource) {
      this.showModal(instance, modalElement);
      return;
    }

    const differentServices = party?.services?.length > 0 &&
      !isEqual(resource.services.sort(), party?.services.sort());
    if (differentServices) {
      instance.clearServicesResources();
    }

    instance.addResourceToSelectedOnes(overrideWithResourceId, resource.services);
    instance.revealForm = (): void => {
      this.showModal(instance, modalElement);
    };
    instance.abortHandOff = (): void => {
      void modalElement.dismiss(null);
    };
    instance.resourceOverrideCallback = (newParty: IParty): void => {
      const extraConflicts = {
        amountOfServicesChanged: party?.services.length > 0 && party?.services.length !== newParty.services.length,
        wrongResourceSize: party?.size < selectedResource.capacity.min || party?.size > selectedResource.capacity.max,
        overlapping: instance.showOverlappingAlert,
        wrongPartySize: !!instance.resourceSizeAlert,
        blocked: instance.isReservation && Boolean(instance.blockedTime?.blocked) && !instance.overrideBlockedTime,
      };

      if (extraConflicts.blocked) {
        this.showModal(instance, modalElement);
        return;
      }

      if (differentServices) {
        instance.selectServices();
        this.showModal(instance, modalElement);
        return;
      }

      if (extraConflicts.overlapping) {
        this.showModal(instance, modalElement);
        return;
      }

      if (extraConflicts.wrongResourceSize) {
        instance.showSizeAlert(selectedResource.capacity);
        this.showModal(instance, modalElement);
        return;
      }

      if (extraConflicts.wrongPartySize) {
        this.showModal(instance, modalElement);
        return;
      }

      if (extraConflicts.amountOfServicesChanged) {
        instance.sizeChanged();
        instance.selectResources();
        this.showModal(instance, modalElement);
        return;
      }

      const conflicts = instance.doesEditingHaveAnyConflicts();
      let conflictsCount = 0;
      Object.entries(conflicts).forEach(([, state]) => {
        if (state) {
          conflictsCount += 1;
        }
      });

      const fixDuration = (callback: () => void): void => {
        instance.selectServices();
        instance.resourceOverrideCallback = (): void => {
          callback();
        };
      };

      if (conflictsCount > 0) {
        if (conflictsCount === 1 && serveNow && conflicts['editFormInvalid']) {
          fixDuration(() => {
            this.closeModal(instance);
          });
        }

        this.showModal(instance, modalElement);
        return;
      }

      if (serveNow) {
        fixDuration(() => {
          this.showModal(instance, modalElement);
        });
        return;
      }

      if (!newParty?.duration) {
        fixDuration(() => {
          this.closeModal(instance, showSuccessUpdateAlert);
        });
      } else {
        this.closeModal(instance);
      }
    };
  }

  private isArray(val: any): boolean {
    return val instanceof Array;
  }

  private isString(str: any): boolean {
    return str != null && typeof str.valueOf() === 'string';
  }
}

interface AddEditPartyReactive {
  user: AuthUser;
  parties: Party[];
  schedules: Schedule[];
  showSelectResourceMessage: boolean;
  prefillFields?: Partial<Party>;
  party?: Party;
}
